import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { ListChildComponentProps } from 'react-window';

import { makeStyles } from '@material-ui/core';

import clsx from 'clsx';

import { AppDispatch } from '../app/store';
import Exposure from '../components/Exposure';
import UserCell, { UserCellLoading } from '../components/UserCell';
import { showStudentDialog } from '../home/reducer';
import { colors } from '../utils/theme';
import { StudentCovidDataForCourse } from './types';

export const AffectedCoursesCellHeight = 65;

const useStyles = makeStyles((theme) => ({
  root: {
    cursor: 'pointer',
    display: 'grid',
    padding: theme.spacing(1, 0),
    height: AffectedCoursesCellHeight,
    gridTemplateColumns: '2fr 1fr 1fr',
    gridTemplateAreas: '"user lectures-attended status"',
    borderBottom: '1px solid rgba(186, 186, 186, 0.5)',
    textAlign: 'right',

    '&:hover': {
      backgroundColor: '#eee',
    },
  },
  user: {
    gridArea: 'user',
    alignSelf: 'center',
    marginLeft: theme.spacing(2),
    textAlign: 'left',
  },
  cellHeader: {
    color: 'rgba(51, 51, 51, 0.5)',
    height: '1rem',
    fontSize: '0.75rem',
    fontWeight: 'bold',
    textTransform: 'uppercase',
  },
  cellValue: {
    fontSize: '1.125rem',
    fontWeight: 'bold',
    lineHeight: 1.78,
    color: colors.grey1,
  },
  lecturesAttended: {
    gridArea: 'lectures-attended',
  },
  status: {
    gridArea: 'status',
  },
  loaderStrip: theme.mixins.loaderStrip,
  loaderAvatar: theme.mixins.loaderAvatar,
}));

const AffectedCoursesCell = ({
  index,
  isScrolling,
  style,
  data,
}: ListChildComponentProps<SparseArray<StudentCovidDataForCourse>>) => {
  const styles = useStyles();
  const dispatch = useDispatch<AppDispatch>();

  const student = data[index];

  const handleClick = useCallback(() => {
    if (!student?.userId) return;
    dispatch(showStudentDialog({ studentId: student.userId }));
  }, [dispatch, student?.userId]);

  if (!student || isScrolling) {
    return (
      <div className={styles.root} style={style}>
        <UserCellLoading className={styles.user} />
        <div className={styles.lecturesAttended}>
          <div className={styles.cellHeader}>
            <div className={clsx(styles.loaderStrip, 'right')} />
          </div>
          <div className={styles.cellValue}>
            <div className={clsx(styles.loaderStrip, 'right', 'tall', 'half')} />
          </div>
        </div>
        <div className={styles.status}>
          <div className={styles.cellHeader}>
            <div className={clsx(styles.loaderStrip, 'right')} />
          </div>
          <div className={styles.cellValue}>
            <div className={clsx(styles.loaderStrip, 'right', 'tall', 'half')} />
          </div>
        </div>
      </div>
    );
  }

  const { avatar, name, emailId, status, proximity, lecturesAttended } = student;

  return (
    <div className={styles.root} style={style} onClick={handleClick}>
      <UserCell className={styles.user} avatarURL={avatar} name={name} email={emailId} />
      <div className={styles.lecturesAttended}>
        <div className={styles.cellHeader}>Lectures attended</div>
        <div className={styles.cellValue}>{lecturesAttended}</div>
      </div>
      <div className={styles.status}>
        <div className={styles.cellHeader}>Status</div>
        <Exposure className={styles.cellValue} covidStatus={status} proximity={proximity} />
      </div>
    </div>
  );
};

export default AffectedCoursesCell;
